import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Grid, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Paper, 
  Avatar, 
  Chip, 
  Card, 
  CardContent, 
  IconButton, 
  Divider, 
  Alert, 
  Fade, 
  Zoom, 
  useMediaQuery,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Tabs,
  Tab,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  ArrowBack,
  CloudUpload,
  Palette,
  Preview,
  Save,
  TrendingUp,
  Star,
  Verified,
  Public,
  PublicOff,
  QrCode,
  Animation,
  ViewModule,
  ViewQuilt,
  Info,
  CheckCircle,
  Visibility,
  VisibilityOff,
  HelpOutline,
  Launch,
  Settings
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useDropzone } from 'react-dropzone';
import API from '../../../API';
import useAuth from '../../../Hooks/use-auth';

// Enhanced Color Templates with background colors
const COLOR_TEMPLATES = [
  { name: 'Ocean Breeze', main: '#6F72FF', accent: '#BA88FF', bg: '#F8FAFF' },
  { name: 'Sunset Vibes', main: '#FF5733', accent: '#C70039', bg: '#FFF8F6' },
  { name: 'Forest Green', main: '#2E7D32', accent: '#81C784', bg: '#F1F8E9' },
  { name: 'Royal Purple', main: '#7B1FA2', accent: '#CE93D8', bg: '#F3E5F5' },
  { name: 'Electric Blue', main: '#1E88E5', accent: '#42A5F5', bg: '#E3F2FD' },
  { name: 'Warm Orange', main: '#FB8C00', accent: '#FFB74D', bg: '#FFF3E0' },
  { name: 'Rose Gold', main: '#E91E63', accent: '#F8BBD9', bg: '#FCE4EC' },
  { name: 'Midnight', main: '#263238', accent: '#607D8B', bg: '#ECEFF1' },
];

const ANIMATION_TYPES = [
  { value: 'FADE_IN', label: 'Fade In', icon: '✨', description: 'Smooth fade-in effect' },
  { value: 'SLIDE_UP', label: 'Slide Up', icon: '⬆️', description: 'Slides up from bottom' },
  { value: 'BOUNCE_IN', label: 'Bounce In', icon: '🎾', description: 'Bouncy entrance effect' },
  { value: 'ZOOM_IN', label: 'Zoom In', icon: '🔍', description: 'Scales in with zoom' }
];

const FILE_SIZE_LIMIT = 4.5 * 1024 * 1024;

// Enhanced Styled Components
const HeroSection = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  padding: theme.spacing(6, 0),
  borderRadius: '0 0 32px 32px',
  position: 'relative',
  overflow: 'hidden',
  marginBottom: theme.spacing(4),
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 30% 20%, ${alpha(theme.palette.common.white, 0.1)} 0%, transparent 50%)`,
  },
}));

const GlassCard = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(145deg, 
    ${alpha(theme.palette.background.paper, 0.95)}, 
    ${alpha(theme.palette.background.paper, 0.9)}
  )`,
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  overflow: 'hidden',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.common.white, 0.5)}, transparent)`,
  },
}));

const SectionCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '16px',
  marginBottom: theme.spacing(3),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  background: `linear-gradient(145deg, 
    ${alpha(theme.palette.background.paper, 0.95)}, 
    ${alpha(theme.palette.background.paper, 0.9)}
  )`,
}));

const DropZone = styled(Box)(({ theme, isDragActive, hasFile }) => ({
  border: hasFile 
    ? `2px solid ${theme.palette.success.main}` 
    : `2px dashed ${isDragActive ? theme.palette.primary.main : alpha(theme.palette.divider, 0.3)}`,
  borderRadius: '16px',
  padding: theme.spacing(4),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  background: hasFile
    ? alpha(theme.palette.success.main, 0.05)
    : isDragActive 
      ? alpha(theme.palette.primary.main, 0.05)
      : alpha(theme.palette.background.paper, 0.5),
  '&:hover': {
    borderColor: theme.palette.primary.main,
    background: alpha(theme.palette.primary.main, 0.05),
  },
}));

const ColorTemplate = styled(Box)(({ theme, isSelected }) => ({
  width: 80,
  height: 50,
  borderRadius: '12px',
  cursor: 'pointer',
  border: isSelected 
    ? `3px solid ${theme.palette.primary.main}` 
    : `2px solid ${alpha(theme.palette.divider, 0.2)}`,
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
  },
  '&::after': isSelected ? {
    content: '"✓"',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: theme.palette.common.white,
    fontWeight: 'bold',
    fontSize: '18px',
    textShadow: '0 2px 4px rgba(0,0,0,0.5)',
  } : {},
}));

const PreviewCard = styled(Card)(({ theme, maincolor, accentcolor }) => ({
  borderRadius: '20px',
  background: `linear-gradient(145deg, 
    ${alpha(theme.palette.background.paper, 0.9)}, 
    ${alpha(theme.palette.background.paper, 0.7)}
  )`,
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 24px ${alpha(maincolor || theme.palette.primary.main, 0.2)}`,
  },
}));

const ModernButton = styled(Button)(({ theme, variant, bgcolor }) => ({
  borderRadius: '12px',
  fontWeight: 600,
  textTransform: 'none',
  padding: theme.spacing(1.5, 3),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  ...(variant === 'contained' && bgcolor && {
    background: `linear-gradient(135deg, ${bgcolor}, ${alpha(bgcolor, 0.8)})`,
    '&:hover': {
      background: `linear-gradient(135deg, ${alpha(bgcolor, 0.9)}, ${alpha(bgcolor, 0.7)})`,
      transform: 'translateY(-2px)',
      boxShadow: `0 8px 16px ${alpha(bgcolor, 0.3)}`,
    },
  }),
}));

const LayoutCard = styled(Card)(({ theme, isSelected }) => ({
  cursor: 'pointer',
  border: isSelected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
  borderRadius: '12px',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const SwitchCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: '12px',
  marginBottom: theme.spacing(1.5),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  background: `linear-gradient(145deg, 
    ${alpha(theme.palette.background.paper, 0.95)}, 
    ${alpha(theme.palette.background.paper, 0.9)}
  )`,
}));

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const EnhancedAgencyRosterDesktop = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { getCurrrentUser } = useAuth();
  const userInfo = getCurrrentUser();
  const agencyName = userInfo?.company_name;
  
  // Tab state
  const [currentTab, setCurrentTab] = useState(0);
  
  // Form state - expanded with new features
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    logo: '',
    viewType: 'SIMPLE',
    animationsEnabled: true,
    animationType: 'FADE_IN',
    headerMediaUrl: '',
    colorScheme: {
      main: '#6F72FF',
      accent: '#BA88FF',
      backgroundColor: '#F8FAFF'
    },
    isPublic: true,
    qrEnabled: false,
    showStats: true,
    showContactInfo: false,
    seoTitle: '',
    seoDescription: '',
    customCss: ''
  });
  
  const [selectedTemplate, setSelectedTemplate] = useState(0);
  const [settingsExists, setSettingsExists] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const queryClient = useQueryClient();

  // Fetch existing settings with enhanced data structure
  const { isLoading } = useQuery(
    ['rosterSettings', agencyName],
    () => API.agency.getRosterSettings(agencyName),
    {
      retry: false,
      onSuccess: (data) => {
        setSettingsExists(true);
        setFormData({
          name: data.name || '',
          slug: data.slug || '',
          description: data.description || '',
          logo: data.logo || '',
          viewType: data.view_type || 'SIMPLE',
          animationsEnabled: data.animations_enabled !== false,
          animationType: data.animation_type || 'FADE_IN',
          headerMediaUrl: data.header_media_url || '',
          colorScheme: data.color_scheme || {
            main: '#6F72FF',
            accent: '#BA88FF',
            backgroundColor: '#F8FAFF'
          },
          isPublic: data.is_public !== false,
          qrEnabled: data.qr_enabled || false,
          showStats: data.show_stats !== false,
          showContactInfo: data.show_contact_info || false,
          seoTitle: data.seo_title || '',
          seoDescription: data.seo_description || '',
          customCss: data.custom_css || ''
        });
        
        // Find matching template
        const matchingTemplate = COLOR_TEMPLATES.findIndex(
          (tpl) => tpl.main === data.color_scheme?.main && 
                   tpl.accent === data.color_scheme?.accent &&
                   tpl.bg === data.color_scheme?.backgroundColor
        );
        if (matchingTemplate !== -1) {
          setSelectedTemplate(matchingTemplate);
        }
      },
      onError: (err) => {
        if (err?.response?.status === 404) {
          setSettingsExists(false);
          setFormData(prev => ({
            ...prev,
            name: `Creator Roster by: ${agencyName}`,
            description: 'A curated list of creators and all their social stats.'
          }));
        } else {
          setErrorMsg('Failed to fetch roster settings.');
        }
      },
    }
  );

  // File upload mutation with field support
  const { mutate: uploadFile, isLoading: isUploading } = useMutation(
    (fileData) => API.files.post(fileData),
    {
      onSuccess: (data, variables) => {
        const field = variables.field;
        setFormData(prev => ({ ...prev, [field]: data.url }));
        setSuccessMsg(`${field === 'logo' ? 'Logo' : 'Header media'} uploaded successfully!`);
        setTimeout(() => setSuccessMsg(''), 3000);
      },
      onError: (error) => {
        setErrorMsg(`Error uploading file: ${error.message}`);
      },
    }
  );

  // Enhanced save settings mutations
  const createMutation = useMutation(
    (payload) => API.agency.createRosterSettings(agencyName, payload),
    {
      onSuccess: () => {
        setSuccessMsg('Roster settings created successfully!');
        setSettingsExists(true);
        setTimeout(() => setSuccessMsg(''), 3000);
      },
      onError: (err) => {
        setErrorMsg(err?.response?.data?.error || 'Failed to create roster settings.');
      },
    }
  );

  const updateMutation = useMutation(
    (payload) => API.agency.updateRosterSettings(agencyName, payload),
    {
      onSuccess: () => {
        setSuccessMsg('Roster settings updated successfully!');
        setTimeout(() => setSuccessMsg(''), 3000);
      },
      onError: (err) => {
        setErrorMsg(err?.response?.data?.error || 'Failed to update roster settings.');
      },
    }
  );

  // Update form data helpers
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const updateColorScheme = (field, value) => {
    setFormData(prev => ({
      ...prev,
      colorScheme: { ...prev.colorScheme, [field]: value }
    }));
    setSelectedTemplate(null);
  };

  // Enhanced file upload handlers
  // FIXED: Direct handlers instead of factory function
const logoDropHandler = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (file.size > FILE_SIZE_LIMIT) {
      setErrorMsg('File size exceeds the 4.5MB limit. Please upload a smaller file.');
      return;
    }

    console.log('💻 Desktop: Uploading logo:', file.name);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64File = reader.result.split(',')[1];
      uploadFile({
        field: 'logo',  // ✅ Hardcoded field name
        file: {
          name: file.name,
          content: base64File,
          type: file.type,
        },
      });
    };
  }, [uploadFile]);

  const headerDropHandler = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (file.size > FILE_SIZE_LIMIT) {
      setErrorMsg('File size exceeds the 4.5MB limit. Please upload a smaller file.');
      return;
    }

    console.log('💻 Desktop: Uploading header media:', file.name);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64File = reader.result.split(',')[1];
      uploadFile({
        field: 'headerMediaUrl',  // ✅ Hardcoded field name
        file: {
          name: file.name,
          content: base64File,
          type: file.type,
        },
      });
    };
  }, [uploadFile]);

  const { getRootProps: getLogoRootProps, getInputProps: getLogoInputProps, isDragActive: isLogoDragActive } = useDropzone({ 
    onDrop: logoDropHandler,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp'] }
  });

  const { getRootProps: getHeaderRootProps, getInputProps: getHeaderInputProps, isDragActive: isHeaderDragActive } = useDropzone({ 
    onDrop: headerDropHandler,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp'], 'video/*': ['.mp4', '.webm'] }
  });

  // Enhanced save handler with validation
  const handleSave = () => {
    setErrors({});
    setErrorMsg('');
    
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.slug.trim()) newErrors.slug = 'URL slug is required';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setCurrentTab(0);
      return;
    }
    
    const payload = {
      name: formData.name,
      slug: formData.slug,
      description: formData.description,
      logo: formData.logo,
      view_type: formData.viewType,
      animations_enabled: formData.animationsEnabled,
      animation_type: formData.animationType,
      header_media_url: formData.headerMediaUrl,  // This should be populated
      color_scheme: formData.colorScheme,
      is_public: formData.isPublic,
      qr_enabled: formData.qrEnabled,
      show_stats: formData.showStats,
      show_contact_info: formData.showContactInfo,
      seo_title: formData.seoTitle,
      seo_description: formData.seoDescription,
      custom_css: formData.customCss
    };

    // ADD THESE DEBUG LOGS:
    console.log('💻 Desktop: Saving payload:', JSON.stringify(payload, null, 2));
    console.log('📝 Header media URL:', payload.header_media_url);
    console.log('🔗 Slug:', payload.slug);

    if (!settingsExists) {
      createMutation.mutate(payload);
    } else {
      updateMutation.mutate(payload);
    }
  };

  const applyColorTemplate = (template, index) => {
    setFormData(prev => ({
      ...prev,
      colorScheme: {
        main: template.main,
        accent: template.accent,
        backgroundColor: template.bg
      }
    }));
    setSelectedTemplate(index);
  };

  const mockCreators = [
    { name: '@johndoe', followers: '1.2M', platform: 'YouTube', avatar: 'J' },
    { name: '@janesmitha', followers: '800K', platform: 'Instagram', avatar: 'J' },
    { name: '@creativemike', followers: '2.1M', platform: 'TikTok', avatar: 'C' },
  ];

  // Enhanced render methods
  const renderBasicSettings = () => (
    <Stack spacing={4}>
      <SectionCard>
        <Typography variant="h6" gutterBottom color="primary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Info color="primary" />
          Basic Information
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Set up the fundamental details for your creator roster.
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Roster Name"
              value={formData.name}
              onChange={(e) => updateField('name', e.target.value)}
              fullWidth
              error={!!errors.name}
              helperText={errors.name || 'The main title displayed on your roster'}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="URL Slug"
              value={formData.slug}
              onChange={(e) => updateField('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              fullWidth
              error={!!errors.slug}
              helperText={errors.slug || `Available at: /roster/${formData.slug || 'your-slug'}`}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => updateField('description', e.target.value)}
              fullWidth
              multiline
              rows={3}
              helperText="Brief description that appears below your roster title"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard>
        <Typography variant="h6" gutterBottom color="primary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudUpload color="primary" />
          Media Upload
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Add your agency logo and header media to customize your roster appearance.
        </Typography>
        
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle1" gutterBottom fontWeight={600}>Logo</Typography>
            <DropZone {...getLogoRootProps()} isDragActive={isLogoDragActive} hasFile={!!formData.logo}>
              <input {...getLogoInputProps()} />
              {formData.logo ? (
                <Box>
                  <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>Logo Uploaded</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click to change logo
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {isLogoDragActive ? 'Drop your logo here...' : 'Upload Logo'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Drag & drop or click to browse • PNG, JPG, SVG (Max: 4.5MB)
                  </Typography>
                  {isUploading && (
                    <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                      Uploading...
                    </Typography>
                  )}
                </Box>
              )}
            </DropZone>

            {formData.logo && (
              <Zoom in={!!formData.logo}>
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <img
                    src={formData.logo}
                    alt="Logo Preview"
                    style={{ 
                      maxWidth: '200px', 
                      maxHeight: '80px', 
                      borderRadius: '12px',
                      boxShadow: theme.shadows[3]
                    }}
                  />
                </Box>
              </Zoom>
            )}

            <TextField
              label="Or paste logo URL"
              value={formData.logo}
              onChange={(e) => updateField('logo', e.target.value)}
              fullWidth
              sx={{ 
                '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                mt: 2
              }}
            />
          </Box>

          <Box>
            <Typography variant="subtitle1" gutterBottom fontWeight={600}>Header Banner</Typography>
            <DropZone {...getHeaderRootProps()} isDragActive={isHeaderDragActive} hasFile={!!formData.headerMediaUrl}>
              <input {...getHeaderInputProps()} />
              {formData.headerMediaUrl ? (
                <Box>
                  <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>Header Media Added</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click to change media
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {isHeaderDragActive ? 'Drop your header media here...' : 'Upload Header Media'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Banner image or video • Recommended: 1200x400px
                  </Typography>
                </Box>
              )}
            </DropZone>
          </Box>
        </Stack>
      </SectionCard>
    </Stack>
  );

  const renderDesignSettings = () => (
    <Stack spacing={4}>
      <SectionCard>
        <Typography variant="h6" gutterBottom color="primary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ViewModule color="primary" />
          Layout & Design
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Choose how your creators are displayed and customize the visual appearance.
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight={600}>Layout Style</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <LayoutCard 
                isSelected={formData.viewType === 'SIMPLE'}
                onClick={() => updateField('viewType', 'SIMPLE')}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <ViewModule sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h6" gutterBottom>Simple</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Clean, minimal grid layout
                  </Typography>
                </CardContent>
              </LayoutCard>
            </Grid>
            <Grid item xs={6}>
              <LayoutCard 
                isSelected={formData.viewType === 'COMPLEX'}
                onClick={() => updateField('viewType', 'COMPLEX')}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <ViewQuilt sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                  <Typography variant="h6" gutterBottom>Complex</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Rich cards with detailed stats
                  </Typography>
                </CardContent>
              </LayoutCard>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight={600}>Color Themes</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {COLOR_TEMPLATES.map((template, index) => (
              <Grid item key={template.name}>
                <Tooltip title={template.name} arrow>
                  <Box sx={{ textAlign: 'center' }}>
                    <ColorTemplate
                      isSelected={selectedTemplate === index}
                      onClick={() => applyColorTemplate(template, index)}
                      sx={{
                        background: `linear-gradient(135deg, ${template.main} 0%, ${template.accent} 100%)`,
                        mb: 1,
                      }}
                    />
                    <Typography variant="caption" display="block">
                      {template.name}
                    </Typography>
                  </Box>
                </Tooltip>
              </Grid>
            ))}
          </Grid>

          <Typography variant="subtitle1" gutterBottom fontWeight={600}>Custom Colors</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" gutterBottom>Main Color</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <input
                  type="color"
                  value={formData.colorScheme.main}
                  onChange={(e) => updateColorScheme('main', e.target.value)}
                  style={{
                    width: '50px',
                    height: '50px',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                />
                <TextField
                  value={formData.colorScheme.main}
                  onChange={(e) => updateColorScheme('main', e.target.value)}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" gutterBottom>Accent Color</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <input
                  type="color"
                  value={formData.colorScheme.accent}
                  onChange={(e) => updateColorScheme('accent', e.target.value)}
                  style={{
                    width: '50px',
                    height: '50px',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                />
                <TextField
                  value={formData.colorScheme.accent}
                  onChange={(e) => updateColorScheme('accent', e.target.value)}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" gutterBottom>Background Color</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <input
                  type="color"
                  value={formData.colorScheme.backgroundColor}
                  onChange={(e) => updateColorScheme('backgroundColor', e.target.value)}
                  style={{
                    width: '50px',
                    height: '50px',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                />
                <TextField
                  value={formData.colorScheme.backgroundColor}
                  onChange={(e) => updateColorScheme('backgroundColor', e.target.value)}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </SectionCard>

      <SectionCard>
        <Typography variant="h6" gutterBottom color="primary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Animation color="primary" />
          Animation Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Add smooth animations to make your roster more engaging.
        </Typography>
        
        <SwitchCard>
          <FormControlLabel
            control={
              <Switch
                checked={formData.animationsEnabled}
                onChange={(e) => updateField('animationsEnabled', e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <Animation />
                <Box>
                  <Typography variant="body2" fontWeight="bold">Enable Animations</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Add smooth transitions and effects to your roster
                  </Typography>
                </Box>
              </Box>
            }
            sx={{ width: '100%', margin: 0 }}
          />
        </SwitchCard>
        
        {formData.animationsEnabled && (
          <FormControl fullWidth>
            <InputLabel>Animation Type</InputLabel>
            <Select
              value={formData.animationType}
              label="Animation Type"
              onChange={(e) => updateField('animationType', e.target.value)}
              sx={{ borderRadius: '12px' }}
            >
              {ANIMATION_TYPES.map((anim) => (
                <MenuItem key={anim.value} value={anim.value}>
                  <Box display="flex" alignItems="center" gap={2} py={1}>
                    <span style={{ fontSize: '1.2rem' }}>{anim.icon}</span>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">{anim.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {anim.description}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Choose how items appear when the page loads</FormHelperText>
          </FormControl>
        )}
      </SectionCard>
    </Stack>
  );

  const renderPrivacySettings = () => (
    <Stack spacing={4}>
      <SectionCard>
        <Typography variant="h6" gutterBottom color="primary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Settings color="primary" />
          Privacy & Features
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Control who can see your roster and what information is displayed.
        </Typography>
        
        <Stack spacing={2}>
          <SwitchCard>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isPublic}
                  onChange={(e) => updateField('isPublic', e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  {formData.isPublic ? <Public /> : <PublicOff />}
                  <Box>
                    <Typography variant="body2" fontWeight="bold">Public Roster</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formData.isPublic ? 'Anyone can view your roster' : 'Roster is private'}
                    </Typography>
                  </Box>
                </Box>
              }
              sx={{ width: '100%', margin: 0 }}
            />
          </SwitchCard>

          <SwitchCard>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.qrEnabled}
                  onChange={(e) => updateField('qrEnabled', e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <QrCode />
                  <Box>
                    <Typography variant="body2" fontWeight="bold">QR Code Sharing</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Generate QR code for easy sharing
                    </Typography>
                  </Box>
                </Box>
              }
              sx={{ width: '100%', margin: 0 }}
            />
          </SwitchCard>

          <SwitchCard>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.showStats}
                  onChange={(e) => updateField('showStats', e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <TrendingUp />
                  <Box>
                    <Typography variant="body2" fontWeight="bold">Show Creator Statistics</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Display follower counts and engagement metrics
                    </Typography>
                  </Box>
                </Box>
              }
              sx={{ width: '100%', margin: 0 }}
            />
          </SwitchCard>

          <SwitchCard>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.showContactInfo}
                  onChange={(e) => updateField('showContactInfo', e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  {formData.showContactInfo ? <Visibility /> : <VisibilityOff />}
                  <Box>
                    <Typography variant="body2" fontWeight="bold">Show Contact Information</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Display creator contact details
                    </Typography>
                  </Box>
                </Box>
              }
              sx={{ width: '100%', margin: 0 }}
            />
          </SwitchCard>
        </Stack>
      </SectionCard>

      <SectionCard>
        <Typography variant="h6" gutterBottom color="primary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpOutline color="primary" />
          SEO & Advanced
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Optimize your roster for search engines and add custom styling.
        </Typography>
        
        <Stack spacing={3}>
          <TextField
            label="SEO Title"
            value={formData.seoTitle}
            onChange={(e) => updateField('seoTitle', e.target.value)}
            fullWidth
            helperText="Custom page title for search engines (recommended: 50-60 characters)"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          />
          
          <TextField
            label="SEO Description"
            value={formData.seoDescription}
            onChange={(e) => updateField('seoDescription', e.target.value)}
            fullWidth
            multiline
            rows={3}
            helperText="Description that appears in search results (recommended: 150-160 characters)"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          />

          <TextField
            label="Custom CSS"
            value={formData.customCss}
            onChange={(e) => updateField('customCss', e.target.value)}
            fullWidth
            multiline
            rows={6}
            helperText="Advanced users only: Add custom CSS to override default styles"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            placeholder="/* Add your custom CSS here */"
            inputProps={{ style: { fontFamily: 'monospace' } }}
          />
        </Stack>
      </SectionCard>
    </Stack>
  );

  const renderPreview = () => (
    <Box>
      <Typography variant="h6" gutterBottom color="primary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Preview color="primary" />
        Live Preview
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        See how your roster will look to visitors in real-time.
      </Typography>
      
      <Paper 
        elevation={4}
        sx={{ 
          borderRadius: '16px',
          overflow: 'hidden',
          bgcolor: formData.colorScheme.backgroundColor,
          minHeight: 500,
          border: `2px solid ${alpha(theme.palette.divider, 0.1)}`
        }}
      >
        {/* Header Preview */}
        <Box sx={{ 
          background: `linear-gradient(135deg, ${formData.colorScheme.main} 0%, ${formData.colorScheme.accent} 100%)`,
          p: 4,
          color: 'white',
          textAlign: 'center',
          position: 'relative'
        }}>
          {formData.logo && (
            <Box sx={{ mb: 2 }}>
              <img
                src={formData.logo}
                alt="Logo"
                style={{ 
                  height: '50px', 
                  borderRadius: '8px',
                }}
              />
            </Box>
          )}
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {formData.name || 'Your Roster Name'}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, maxWidth: 600, mx: 'auto' }}>
            {formData.description || 'Your roster description will appear here'}
          </Typography>
        </Box>

        {/* Creator Cards Preview */}
        <Box sx={{ p: 3 }}>
          <Typography variant="h5" fontWeight={600} gutterBottom sx={{ mb: 3 }}>
            Our Creators
          </Typography>
          <Grid container spacing={2}>
            {mockCreators.map((creator, index) => (
              <Grid item xs={12} md={formData.viewType === 'COMPLEX' ? 6 : 12} key={index}>
                <PreviewCard maincolor={formData.colorScheme.main} accentcolor={formData.colorScheme.accent}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ 
                        bgcolor: formData.colorScheme.main, 
                        width: formData.viewType === 'COMPLEX' ? 56 : 48, 
                        height: formData.viewType === 'COMPLEX' ? 56 : 48,
                        fontSize: formData.viewType === 'COMPLEX' ? '1.5rem' : '1.25rem'
                      }}>
                        {creator.avatar}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant={formData.viewType === 'COMPLEX' ? 'h6' : 'subtitle1'} fontWeight={600}>
                          {creator.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {creator.platform} • {formData.showStats ? creator.followers : 'Content Creator'}
                        </Typography>
                        {formData.viewType === 'COMPLEX' && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Specializing in lifestyle and tech content
                          </Typography>
                        )}
                        {formData.showContactInfo && (
                          <Typography variant="caption" color="text.secondary">
                            contact@{creator.name.slice(1)}.com
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        icon={<TrendingUp />}
                        label="Active"
                        size="small"
                        sx={{ 
                          bgcolor: alpha(formData.colorScheme.accent, 0.1),
                          color: formData.colorScheme.main,
                          borderRadius: '8px',
                        }}
                      />
                    </Box>
                  </CardContent>
                </PreviewCard>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Paper>

      <Alert severity="info" sx={{ mt: 3, borderRadius: '12px' }}>
        <Typography variant="body2">
          <strong>Preview Note:</strong> This shows how your roster will appear to visitors. 
          {formData.animationsEnabled && ` Animations (${formData.animationType.replace('_', ' ')}) will be visible on the live site.`}
        </Typography>
      </Alert>
    </Box>
  );

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box textAlign="center">
          <Typography variant="h4" gutterBottom color="primary">
            Loading Roster Settings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Preparing your enhanced roster management interface...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
      {/* Enhanced Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <IconButton
                onClick={() => navigate('/agency')}
                sx={{ 
                  mr: 2, 
                  color: 'white',
                  bgcolor: alpha(theme.palette.common.white, 0.1),
                  '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.2) }
                }}
              >
                <ArrowBack />
              </IconButton>
              <Typography variant="h3" sx={{ 
                fontWeight: 900, 
                color: 'white',
                letterSpacing: '-0.02em',
              }}>
                Enhanced Roster Settings
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ 
              color: alpha(theme.palette.common.white, 0.9),
              maxWidth: 700,
              lineHeight: 1.6,
            }}>
              Create and customize your agency's creator roster with advanced design controls, 
              privacy settings, SEO optimization, and animation effects.
            </Typography>
            
            {/* Status badges */}
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 3 }}>
              <Chip
                icon={formData.isPublic ? <Public /> : <PublicOff />}
                label={formData.isPublic ? 'Public' : 'Private'}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  color: 'white',
                  backdropFilter: 'blur(10px)'
                }}
              />
              {formData.animationsEnabled && (
                <Chip
                  icon={<Animation />}
                  label={`${formData.animationType.replace('_', ' ')} Animations`}
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    color: 'white',
                    backdropFilter: 'blur(10px)'
                  }}
                />
              )}
              <Chip
                label={`${formData.viewType} Layout`}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  color: 'white',
                  backdropFilter: 'blur(10px)'
                }}
              />
              {formData.qrEnabled && (
                <Chip
                  icon={<QrCode />}
                  label="QR Enabled"
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    color: 'white',
                    backdropFilter: 'blur(10px)'
                  }}
                />
              )}
            </Stack>
          </Box>
        </Container>
      </HeroSection>

      <Container maxWidth="lg" sx={{ mt: -4, pb: 6, position: 'relative', zIndex: 3 }}>
        {/* Alerts */}
        {errorMsg && (
          <Fade in={!!errorMsg}>
            <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setErrorMsg('')}>
              {errorMsg}
            </Alert>
          </Fade>
        )}
        {successMsg && (
          <Fade in={!!successMsg}>
            <Alert severity="success" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setSuccessMsg('')}>
              {successMsg}
            </Alert>
          </Fade>
        )}

        <Grid container spacing={4}>
          {/* Enhanced Settings Panel */}
          <Grid item xs={12} lg={8}>
            <GlassCard sx={{ p: 0 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs 
                  value={currentTab} 
                  onChange={(e, newValue) => setCurrentTab(newValue)}
                  variant="fullWidth"
                  sx={{ 
                    px: 3,
                    '& .MuiTab-root': { 
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '1rem',
                      minHeight: 64
                    }
                  }}
                >
                  <Tab 
                    label={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Info />
                        <span>Basic Info</span>
                      </Box>
                    }
                  />
                  <Tab 
                    label={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Palette />
                        <span>Design</span>
                      </Box>
                    }
                  />
                  <Tab 
                    label={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Settings />
                        <span>Privacy & SEO</span>
                      </Box>
                    }
                  />
                  <Tab 
                    label={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Preview />
                        <span>Preview</span>
                      </Box>
                    }
                  />
                </Tabs>
              </Box>

              <Box sx={{ p: 3 }}>
                <TabPanel value={currentTab} index={0}>
                  {renderBasicSettings()}
                </TabPanel>

                <TabPanel value={currentTab} index={1}>
                  {renderDesignSettings()}
                </TabPanel>

                <TabPanel value={currentTab} index={2}>
                  {renderPrivacySettings()}
                </TabPanel>

                <TabPanel value={currentTab} index={3}>
                  {renderPreview()}
                </TabPanel>
              </Box>

              {/* Enhanced Action Buttons */}
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                p: 3, 
                pt: 0, 
                borderTop: currentTab !== 3 ? `1px solid ${alpha(theme.palette.divider, 0.1)}` : 'none' 
              }}>
                <ModernButton
                  variant="contained"
                  bgcolor={formData.colorScheme.main}
                  onClick={handleSave}
                  disabled={createMutation.isLoading || updateMutation.isLoading}
                  startIcon={<Save />}
                  sx={{ flex: 2 }}
                >
                  {createMutation.isLoading || updateMutation.isLoading 
                    ? 'Saving...' 
                    : settingsExists ? 'Update Settings' : 'Create Settings'
                  }
                </ModernButton>
                <ModernButton
                  variant="outlined"
                  onClick={() => setIsPreviewOpen(true)}
                  startIcon={<Preview />}
                  sx={{ 
                    borderColor: formData.colorScheme.main, 
                    color: formData.colorScheme.main,
                    flex: 1
                  }}
                >
                  Full Preview
                </ModernButton>
                <ModernButton
                  variant="outlined"
                  onClick={() => navigate(`/roster/${agencyName}`)}
                  startIcon={<Launch />}
                  sx={{ 
                    borderColor: formData.colorScheme.accent, 
                    color: formData.colorScheme.accent,
                    flex: 1
                  }}
                >
                  View Live
                </ModernButton>
              </Box>
            </GlassCard>
          </Grid>

          {/* Enhanced Quick Preview Sidebar */}
          <Grid item xs={12} lg={4}>
            <Box sx={{ position: 'sticky', top: 24 }}>
              <GlassCard>
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Preview color="primary" />
                    Quick Preview
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  {/* Mini Header Preview */}
                  <Box sx={{ 
                    background: `linear-gradient(135deg, ${formData.colorScheme.main} 0%, ${formData.colorScheme.accent} 100%)`,
                    borderRadius: '12px',
                    p: 2,
                    mb: 3,
                    color: 'white',
                    textAlign: 'center'
                  }}>
                    {formData.logo && (
                      <img
                        src={formData.logo}
                        alt="Logo"
                        style={{ 
                          height: '30px', 
                          marginBottom: '8px',
                          borderRadius: '6px',
                        }}
                      />
                    )}
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      {formData.name || 'Roster Name'}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.8rem' }}>
                      {formData.description || 'Description...'}
                    </Typography>
                  </Box>

                  {/* Configuration Summary */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Configuration
                    </Typography>
                    <Stack spacing={1}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">URL:</Typography>
                        <Chip 
                          label={`/roster/${formData.slug || 'your-slug'}`} 
                          size="small" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      </Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">Layout:</Typography>
                        <Chip 
                          label={formData.viewType} 
                          size="small" 
                          color={formData.viewType === 'SIMPLE' ? 'primary' : 'secondary'}
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      </Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">Visibility:</Typography>
                        <Chip 
                          label={formData.isPublic ? 'Public' : 'Private'} 
                          size="small" 
                          color={formData.isPublic ? 'success' : 'default'}
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      </Box>
                    </Stack>
                  </Box>

                  {/* Mini Creator Cards */}
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Creator Cards:
                  </Typography>
                  <Stack spacing={1}>
                    {mockCreators.slice(0, 2).map((creator, index) => (
                      <Card key={index} sx={{ borderRadius: '8px' }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ bgcolor: formData.colorScheme.main, width: 32, height: 32, fontSize: '0.9rem' }}>
                              {creator.avatar}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {creator.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {creator.platform} • {formData.showStats ? creator.followers : 'Creator'}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>

                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                    Click "Full Preview" to see the complete roster layout with all features
                  </Typography>
                </Box>
              </GlassCard>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Enhanced Full Preview Dialog */}
      <Dialog 
        fullScreen 
        open={isPreviewOpen} 
        onClose={() => setIsPreviewOpen(false)}
        sx={{ '& .MuiDialog-paper': { bgcolor: formData.colorScheme.backgroundColor } }}
      >
        <DialogTitle sx={{ 
          p: 0, 
          background: `linear-gradient(135deg, ${formData.colorScheme.main} 0%, ${formData.colorScheme.accent} 100%)`,
        }}>
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', alignItems: 'center', py: 2, color: 'white' }}>
              <IconButton
                edge="start"
                color="inherit"
                onClick={() => setIsPreviewOpen(false)}
                sx={{ mr: 2 }}
              >
                <ArrowBack />
              </IconButton>
              <Typography variant="h6" sx={{ flex: 1 }}>
                Full Roster Preview • Enhanced View
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mr: 2 }}>
                {formData.animationsEnabled && (
                  <Chip
                    label={formData.animationType.replace('_', ' ')}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                )}
                <Chip
                  label={formData.viewType}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
              </Stack>
              <Typography variant="body2">
                /roster/{formData.slug}
              </Typography>
            </Box>
          </Container>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: formData.colorScheme.backgroundColor }}>
          <Container maxWidth="lg" sx={{ py: 4 }}>
            {renderPreview()}
          </Container>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default EnhancedAgencyRosterDesktop;